/**
 * The line in flight: the vertices the SME has clicked, in image coordinates, and the
 * vertices undone and waiting for a redo.
 *
 * The canvas holds one draft while a line is drawn, renders it, and reports the finished
 * line as the box `toDrawBox` gives. A vertex carries the marked entity it sits on, when
 * a click bound it to one; a free vertex on the paper carries `null`.
 */
import type { BoundingBox } from '../types';

/** The marked entity a vertex sits on. */
export interface VertexBinding {
  kind: 'equipment' | 'instrument' | 'node';
  id: string;
}

export interface LineVertex {
  x: number;
  y: number;
  binding: VertexBinding | null;
}

export interface LineDraft {
  vertices: LineVertex[];
  /** Vertices undone and not yet redone, the last undone last. */
  redo: LineVertex[];
}

/** No line in flight. Clearing a draft is this value: the vertices and the redo history go together. */
export const EMPTY_LINE_DRAFT: LineDraft = { vertices: [], redo: [] };

/** Half the corridor the finished box adds around the path, in image px. */
export const DRAW_BOX_PAD = 10;

/**
 * A new vertex ends the redo history: there is nothing to redo after a new action. A vertex
 * on the last one is refused, as it would add a zero-length segment.
 */
export function addVertex(draft: LineDraft, vertex: LineVertex): LineDraft {
  const last = draft.vertices[draft.vertices.length - 1];
  if (last && last.x === vertex.x && last.y === vertex.y) return draft;
  return { vertices: [...draft.vertices, vertex], redo: [] };
}

export function removeLast(draft: LineDraft): LineDraft {
  if (draft.vertices.length === 0) return draft;
  const last = draft.vertices[draft.vertices.length - 1];
  return { vertices: draft.vertices.slice(0, -1), redo: [...draft.redo, last] };
}

export function redoLast(draft: LineDraft): LineDraft {
  if (draft.redo.length === 0) return draft;
  const last = draft.redo[draft.redo.length - 1];
  return { vertices: [...draft.vertices, last], redo: draft.redo.slice(0, -1) };
}

/** What a finished line's two ends bind to: the first and the last vertex. */
export interface LineEnds {
  source: VertexBinding | null;
  dest: VertexBinding | null;
  /** How many of the line's vertices sit on a marked entity. */
  boundCount: number;
}

export function toLineEnds(draft: LineDraft): LineEnds {
  const first = draft.vertices[0];
  const last = draft.vertices[draft.vertices.length - 1];
  return {
    source: first?.binding ?? null,
    dest: last?.binding ?? null,
    boundCount: draft.vertices.filter((vertex) => vertex.binding).length,
  };
}

/** The flat `[x1, y1, x2, y2, …]` the canvas draws and the saved line stores as `polyline`. */
export function toPolyline(draft: LineDraft): number[] {
  return draft.vertices.flatMap((vertex) => [vertex.x, vertex.y]);
}

/**
 * The box the finished line reports: the vertices' extent padded by `DRAW_BOX_PAD`, with
 * the path itself as `polygon`. A line needs two vertices; fewer give `null`.
 */
export function toDrawBox(draft: LineDraft): BoundingBox | null {
  if (draft.vertices.length < 2) return null;
  const xs = draft.vertices.map((vertex) => vertex.x);
  const ys = draft.vertices.map((vertex) => vertex.y);
  return {
    x1: Math.min(...xs) - DRAW_BOX_PAD,
    y1: Math.min(...ys) - DRAW_BOX_PAD,
    x2: Math.max(...xs) + DRAW_BOX_PAD,
    y2: Math.max(...ys) + DRAW_BOX_PAD,
    polygon: toPolyline(draft),
  };
}
